import gsap from 'gsap'

/**
 * initUI — all DOM/UI helpers that are independent of Three.js.
 *
 * @param {object} sounds    — sounds object from sounds.js
 * @param {object} deps      — { currentUserFn, userProfileDataFn, checkLimit,
 *                               updateLimitDisplay, runOracle }
 *                             All passed as getter-functions so ui.js never
 *                             needs to import auth or oracle modules directly.
 *
 * @returns {{
 *   showMagicAlert, closeMagicAlert,
 *   checkAgeConsent,
 *   updateInterface, updateButtonStates, startCountdown,
 *   updateLimitDisplay, updateLimitUI,
 *   animateCoinFlow,
 *   showInstallInvite
 * }}
 */
export function initUI(sounds, deps) {
    const {
        currentUserFn,
        userProfileDataFn,
        checkLimit,
        runOracle,
        translations
    } = deps

    // ── Magic alert (scroll overlay) ─────────────────────────────────────────
    async function showMagicAlert(message) {
        const overlay  = document.getElementById('magic-alert')
        const wrapper  = document.querySelector('.magic-alert-scroll-wrapper')
        const textElem = document.getElementById('magic-alert-text')
        const btn      = document.getElementById('magic-alert-btn')
        const closeX   = overlay?.querySelector('.close-alert')

        if (!overlay || !textElem || !btn) return

        const oldInput = document.getElementById('user-name-input')
        if (oldInput) oldInput.style.display = 'none'

        // Recreate button to clear old listeners
        const newBtn = btn.cloneNode(true)
        btn.parentNode.replaceChild(newBtn, btn)

        textElem.innerText = message
        newBtn.innerText   = "Continue"

        gsap.killTweensOf([overlay, wrapper])
        overlay.style.visibility  = 'visible'
        overlay.style.opacity     = '1'
        overlay.style.pointerEvents = 'auto'

        gsap.to(overlay, { opacity: 1, duration: 0.3 })
        if (wrapper) {
            gsap.set(wrapper, { scaleY: 0, opacity: 0, transformOrigin: "top" })
            gsap.to(wrapper, { scaleY: 1, opacity: 1, duration: 0.4, ease: "back.out(1.7)" })
        }

        return new Promise((resolve) => {
            const closeAction = () => {
                sounds.click.play()
                gsap.to(overlay, {
                    opacity: 0,
                    duration: 0.2,
                    onComplete: () => {
                        overlay.style.display = 'none'
                        resolve(true)
                    }
                })
            }
            newBtn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); closeAction() }
            if (closeX) closeX.onclick = (e) => { e.preventDefault(); closeAction() }
        })
    }

    function closeMagicAlert() {
        const overlay = document.getElementById('magic-alert')
        const wrapper = document.querySelector('.magic-alert-scroll-wrapper')
        if (!overlay) return

        gsap.killTweensOf([overlay, wrapper])
        if (wrapper) gsap.to(wrapper, { scaleY: 0, opacity: 0, duration: 0.3 })
        gsap.to(overlay, {
            opacity: 0,
            duration: 0.4,
            onComplete: () => {
                overlay.style.setProperty('display', 'none', 'important')
                overlay.style.pointerEvents = 'none'
                overlay.style.visibility    = 'hidden'
            }
        })
    }

    // ── Close buttons init ────────────────────────────────────────────────────
    function initCloseButtons() {
        const profileCloseBtn = document.querySelector('.close-profile')
        if (profileCloseBtn) {
            profileCloseBtn.onclick = () => {
                sounds.click.play()
                document.getElementById('history-overlay').style.display = 'none'
            }
        }
        const alertCloseBtn = document.querySelector('.close-alert')
        if (alertCloseBtn) {
            alertCloseBtn.onclick = () => {
                sounds.click.play()
                closeMagicAlert()
            }
        }
    }
    initCloseButtons()

    // ── Age-consent overlay ───────────────────────────────────────────────────
    function checkAgeConsent(isDaily) {
        if (currentUserFn()) return true

        const hasConsent = localStorage.getItem('age_confirmed')
        if (!hasConsent) {
            const overlay = document.getElementById('age-verification-overlay')
            overlay.style.display = 'flex'

            document.getElementById('confirm-age-btn').onclick = () => {
                localStorage.setItem('age_confirmed', 'true')
                overlay.style.display = 'none'
                sounds.click.play()
                updateLimitDisplay()
                runOracle(isDaily)
            }
            return false
        }
        return true
    }

    // ── Limit display helpers ─────────────────────────────────────────────────
    function updateLimitUI(stats) {
        const limitInfoElem = document.getElementById('limit-info')
        if (limitInfoElem) {
            limitInfoElem.style.color = stats.remaining === 0 ? "#ff4d4d" : ""
            const label = currentUserFn() ? "Questions from a Rabbit friend" : "Questions from guest"
            limitInfoElem.innerText = `${label}: ${stats.remaining}/${stats.limit}`
        }
    }

    function updateLimitDisplay() {
        const limitInfo = document.getElementById('limit-info')
        if (!limitInfo) return

        const currentUser    = currentUserFn()
        const userProfileData = userProfileDataFn()

        if (currentUser) {
            const questionsUsed = userProfileData?.questions_total || 0
            const remaining     = Math.max(0, 3 - questionsUsed)
            limitInfo.innerText = `Questions from a Rabbit friend: ${remaining}/3`
        } else {
            const stats = checkLimit()
            if (!stats) return
            limitInfo.innerText = `Questions from guest: ${Math.max(0, 1 - stats.questionCount)}/1`
        }
    }

    // ── Interface text update ─────────────────────────────────────────────────
    function updateInterface() {
        const t = translations.en
        document.getElementById('name-info').innerText       = t.nameinfo
        document.getElementById('policy-info').innerText     = t.policy
        document.getElementById('question').placeholder      = t.placeholder
        document.getElementById('ask').innerText             = t.revealBtn
        document.getElementById('save-result').innerText     = t.save
        document.getElementById('daily-card').textContent    = t.dailyCardBtn
        updateLimitDisplay()
    }

    // ── Countdown timer ───────────────────────────────────────────────────────
    function startCountdown(element) {
        if (window.countdownInterval) clearInterval(window.countdownInterval)

        function update() {
            const now         = new Date()
            const nextMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
            const diff        = nextMidnight - now

            if (diff <= 0) {
                clearInterval(window.countdownInterval)
                location.reload()
                return
            }

            const h = Math.floor(diff / 3600000)
            const m = Math.floor((diff % 3600000) / 60000)
            const s = Math.floor((diff % 60000) / 1000)
            element.innerText = `RENEWAL IN: ${h}h ${m}m ${s}s`
        }

        update()
        window.countdownInterval = setInterval(update, 1000)
    }

    // ── Button states ─────────────────────────────────────────────────────────
    function updateButtonStates() {
        const stats    = checkLimit()
        const dailyBtn = document.getElementById('daily-card')
        const askBtn   = document.querySelector('.ask-button')
        const timerElem = document.getElementById('countdown-timer')

        if (stats.dailyCardDone && dailyBtn) {
            dailyBtn.style.opacity      = "0.5"
            dailyBtn.style.pointerEvents = "none"
            dailyBtn.innerText          = "Done for today"
        }

        if (currentUserFn()) {
            if (timerElem) {
                timerElem.style.display = "block"
                startCountdown(timerElem)
            }
        } else {
            if (timerElem) timerElem.style.display = "none"
        }

        if (askBtn) {
            if (stats.remaining <= 0) askBtn.classList.add('disabled')
            else                      askBtn.classList.remove('disabled')
        }
    }

    // ── Coin fly animation ────────────────────────────────────────────────────
    function animateCoinFlow() {
        const coin = document.createElement('img')
        coin.src       = './carrot.png'
        coin.className = 'flying-coin'
        Object.assign(coin.style, { position: 'fixed', left: '0px', top: '0px', zIndex: '10000' })
        document.body.appendChild(coin)

        const startX = window.innerWidth  / 2
        const startY = window.innerHeight / 2
        const target = document.getElementById('auth-btn')
        const rect   = target.getBoundingClientRect()
        const endX   = rect.left + rect.width  / 2
        const endY   = rect.top  + rect.height / 2
        const midX   = (startX + endX) / 2
        const midY   = Math.min(startY, endY) - 100

        gsap.set(coin, { x: startX - 15, y: startY - 15, opacity: 0, scale: 0, rotation: 0 })

        const tl = gsap.timeline({ onComplete: () => coin.remove() })

        tl.to(coin, { opacity: 1, scale: 1.8, duration: 0.6, ease: "back.out(1.7)" })
        tl.to(coin, {
            duration: 1.8,
            ease: "power1.inOut",
            motionPath: {
                path: [{ x: startX, y: startY }, { x: midX, y: midY }, { x: endX, y: endY }],
                curviness: 1.5
            },
            x: endX,
            rotation: 1080,
            scale: 0.6
        }, "-=0.2")
        tl.to(coin, { y: midY, duration: 0.9, ease: "power1.out" }, "-=1.8")
        tl.to(coin, { y: endY, duration: 0.9, ease: "power1.in"  }, "-=0.9")
        tl.to(coin, { opacity: 0, scale: 0, duration: 0.3 }, "-=0.2")
        .to(target, { scale: 1.5, filter: "drop-shadow(0 0 15px #fed907)", duration: 0.2, yoyo: true, repeat: 1 }, "-=0.3")
    }

    // ── PWA install invite ────────────────────────────────────────────────────
    function showInstallInvite(platform) {
        if (localStorage.getItem('pwa_invite_closed') === 'true') return

        const text = {
            ios:     "Install Oracle as an app: tap <b>'Share'</b> <img src='https://img.icons8.com/ios/20/ffffff/upload.png'/> and <b>'Add to Home Screen'</b> ✨",
            android: "Install Oracle on your home screen for quick access ✨",
            btn:     "Got it"
        }

        const overlay = document.createElement('div')
        overlay.id = 'pwa-invite'
        overlay.innerHTML = `
            <div style="position:fixed; bottom:20px; left:50%; transform:translateX(-50%);
                        background:rgba(0,0,0,0.9); color:white; padding:15px 20px;
                        border-radius:15px; border:1px solid #fed907; width:90%;
                        z-index:10000; font-family:sans-serif; font-size:14px; text-align:center;
                        box-shadow: 0 0 20px rgba(254, 217, 7, 0.3);">
                <p style="margin:0 0 10px 0;">${platform === 'ios' ? text.ios : text.android}</p>
                <button onclick="this.parentElement.parentElement.remove(); localStorage.setItem('pwa_invite_closed', 'true');"
                        style="background:#fed907; border:none; padding:8px 15px; border-radius:5px; font-weight:bold; cursor:pointer;">
                    ${text.btn}
                </button>
            </div>
        `
        document.body.appendChild(overlay)
    }

    // ── About / Legal overlays (window.* so HTML onclick= can reach them) ────
    window.openAbout = function(id) {
        const overlay = document.getElementById(id)
        if (!overlay) return
        sounds.click.play()
        overlay.style.display    = 'flex'
        document.body.style.overflow = 'hidden'
        gsap.fromTo(overlay,
            { opacity: 0 },
            { opacity: 1, duration: 0.4, ease: "power2.out",
              onComplete: () => gsap.set(overlay, { clearProps: "transform" }) }
        )
    }

    window.closeAbout = function(id) {
        sounds.click.play()
        const overlay = document.getElementById(id)
        if (!overlay) return
        document.querySelector('.webgl').style.touchAction  = 'none'
        document.querySelector('.webgl').style.pointerEvents = 'auto'
        gsap.to(overlay, {
            opacity: 0, duration: 0.3, pointerEvents: "none",
            onComplete: () => {
                overlay.style.display = 'none'
                document.body.classList.remove('overlay-open')
                gsap.set(overlay, { clearProps: "all" })
            }
        })
        document.body.style.overflow = ''
    }

    window.openLegal = function(id) {
        sounds.click.play()
        const overlay = document.getElementById(id)
        if (!overlay) return
        overlay.style.display = 'flex'
        gsap.fromTo(overlay,
            { opacity: 0, scale: 0.95 },
            { opacity: 1, scale: 1, duration: 0.4, pointerEvents: "auto" }
        )
    }

    window.closeLegal = function(id) {
        sounds.click.play()
        const overlay = document.getElementById(id)
        if (!overlay) return
        gsap.to(overlay, {
            opacity: 0, scale: 0.9, duration: 0.3, pointerEvents: "none",
            onComplete: () => {
                overlay.style.display = 'none'
                gsap.set(overlay, { clearProps: "all" })
            }
        })
    }

    // Deep-link via URL hash
    window.addEventListener('load', () => {
        if      (window.location.hash === '#privacy') window.openLegal('privacy-overlay')
        else if (window.location.hash === '#terms')   window.openLegal('terms-overlay')
        else if (window.location.hash === '#about')   window.openAbout('about-overlay')
    })

    // PWA invite on mobile
    const isApp = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
    if (!isApp) {
        window.addEventListener('load', () => {
            const isIOS     = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
            const isAndroid = /Android/.test(navigator.userAgent)
            if (isIOS || isAndroid) {
                setTimeout(() => showInstallInvite(isIOS ? 'ios' : 'android'), 5000)
            }
        })
    }

    // ── Orientation overlay ───────────────────────────────────────────────────
    function handleOrientation() {
        const overlay    = document.getElementById('rotate-overlay')
        const isLandscape = window.innerWidth > window.innerHeight && window.innerHeight < 500

        if (isLandscape) {
            overlay.style.display = 'flex'
            requestAnimationFrame(() => requestAnimationFrame(() => { overlay.style.opacity = '1' }))
            document.body.style.overflow = 'hidden'
        } else {
            overlay.style.opacity = '0'
            setTimeout(() => {
                if (overlay.style.opacity === '0') {
                    overlay.style.display = 'none'
                    document.body.style.overflow = ''
                }
            }, 500)
        }
    }

    window.addEventListener('resize', handleOrientation)
    window.addEventListener('orientationchange', handleOrientation)
    handleOrientation()

    // ── Touch scroll fix for overlays ─────────────────────────────────────────
    document.addEventListener('DOMContentLoaded', () => {
        const scrollableOverlays = [
            '#about-overlay .overlay-scroll-inner',
            '#privacy-overlay .overlay-scroll-inner',
            '#terms-overlay .overlay-scroll-inner',
            '#history-overlay .history-content'
        ]
        scrollableOverlays.forEach(selector => {
            const el = document.querySelector(selector)
            if (!el) return
            el.addEventListener('touchstart', e => e.stopPropagation(), { passive: true })
            el.addEventListener('touchmove',  e => e.stopPropagation(), { passive: true })
            el.addEventListener('touchend',   e => e.stopPropagation(), { passive: true })
        })
    })

    // ── Public API ────────────────────────────────────────────────────────────
    return {
        showMagicAlert,
        closeMagicAlert,
        checkAgeConsent,
        updateInterface,
        updateButtonStates,
        startCountdown,
        updateLimitDisplay,
        updateLimitUI,
        animateCoinFlow,
        showInstallInvite
    }
}
